import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { contactRoles } from '@shared/constants/contact-roles';
import { ContactRole } from '@shared/types/contact-role';
import { GroupType } from '@shared/types/group-type';
import { IonPopover } from '@ionic/angular';
import { startWith } from 'rxjs';

@Component({
  selector: 'app-contact-role',
  templateUrl: './contact-role.component.html',
  styleUrl: './contact-role.component.scss',
  standalone: false,
})
export class ContactRoleComponent implements OnInit {
  @Input() contactRole: ContactRole;
  @Input() groupType: GroupType;
  @Input() contactFormGroups: FormGroup[];
  @Input() contactsFormArray: FormArray;
  @Input() required: boolean = false;
  @Input() groupEdit: boolean = false;
  @Input() formSubmitted: boolean = false;
  @Input() compactDesktop: boolean = false;
  @Input() reviewMode: boolean = false;
  @Output() addContact = new EventEmitter<string>();
  @Output() updateAllCustomers = new EventEmitter();
  @ViewChild('contactPopover') contactPopover: IonPopover;

  contactRoleDetails = contactRoles[0];
  contactRoleTitle: string = '';
  isSingleUseRole: boolean = false;
  assignedContactForms: FormGroup[] = [];
  availableContactForms: FormGroup[] = [];
  contactRoleAssigned: boolean = false;
  showRequiredValidation: boolean = false;
  showActionButton: boolean = true;
  cardAction: { label: string; icon: string } = { label: 'Assign contact', icon: 'person-add-outline' };

  constructor() {}

  ngOnInit(): void {
    this.contactRoleDetails = contactRoles.find(
      (contactRoleDetails) => contactRoleDetails.reference === this.contactRole,
    )!;
    this.contactRoleTitle = this.contactRoleDetails.title;
    if (this.groupType != 'customer' && this.contactRole == 'primary') this.contactRoleTitle = 'Administrator';
    this.isSingleUseRole = !this.contactRoleDetails.allowMultipleContacts;

    this.contactsFormArray.valueChanges.pipe(startWith(null)).subscribe(() => {
      this.assignedContactForms = this.contactFormGroups.filter((contactFormGroup) =>
        contactFormGroup.controls['contact_roles'].value.includes(this.contactRole),
      );
      this.contactRoleAssigned = this.assignedContactForms.length > 0;
      this.showRequiredValidation =
        ((!this.groupEdit && this.formSubmitted) || this.groupEdit) && this.required && !this.contactRoleAssigned;
      this.showActionButton = !this.contactRoleAssigned || !this.isSingleUseRole || this.compactDesktop;
      this.cardAction = !this.contactRoleAssigned
        ? { label: 'Assign contact', icon: 'person-add-outline' }
        : this.isSingleUseRole
          ? { label: 'Reassign contact', icon: 'swap-horizontal-outline' }
          : { label: 'Manage contacts', icon: 'people-outline' };
      this.availableContactForms = this.contactFormGroups.filter(
        (contactForm) => !contactForm.controls['contact_roles'].value.includes(this.contactRole),
      );
    });
  }

  assignContactToRole(contactForm: FormGroup) {
    if (this.isSingleUseRole && this.assignedContactForms[0]) this.removeContactRole(this.assignedContactForms[0]);

    const contactRoles = [...contactForm.controls['contact_roles'].value, this.contactRole];
    contactForm.controls['contact_roles'].setValue(contactRoles);
    contactForm.controls['contact_roles'].markAsTouched();
    contactForm.controls['contact_roles'].markAsDirty();
    contactForm.controls['contact_roles'].updateValueAndValidity();
    this.contactPopover?.dismiss();
  }

  removeContactRole(contactForm: FormGroup) {
    const contactRoles = [...contactForm.controls['contact_roles'].value];
    const newContactRoles = contactRoles.filter((role: ContactRole) => role !== this.contactRole);
    contactForm.controls['contact_roles'].setValue(newContactRoles);
    contactForm.controls['contact_roles'].markAsTouched();
    contactForm.controls['contact_roles'].markAsDirty();
    contactForm.controls['contact_roles'].updateValueAndValidity();
  }

  openPopover(event: Event) {
    if (!this.contactPopover) return;

    this.contactPopover.event = event;
    this.contactPopover.present();
  }

  addNewContactForRole(): void {
    if (this.isSingleUseRole && this.assignedContactForms[0]) this.removeContactRole(this.assignedContactForms[0]);

    this.contactPopover?.dismiss();
    this.addContact.emit(this.contactRole);
  }
}
